import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Plane, ArrowRight, Star } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* Alliance brand colours (close to official palettes, soft for our card bg) */
const ALLIANCE_TONE = {
  'OneWorld':       { bg: '#0F4D8C', text: '#fff' },
  'Star Alliance':  { bg: '#143A6B', text: '#fff' },
  'SkyTeam':        { bg: '#0E8C8A', text: '#fff' },
};

const ISO2_FLAG = (iso2) => {
  if (!iso2 || iso2.length !== 2) return '';
  // Convert ISO-2 country code to flag emoji
  const A = 0x1F1E6;
  return String.fromCodePoint(...iso2.toUpperCase().split('').map(c => A + c.charCodeAt(0) - 65));
};

const AirlineCard = ({ airline }) => {
  const tone = ALLIANCE_TONE[airline.alliance];
  const logoSrc = airline.logo_tail_path ? `${API}${airline.logo_tail_path}` : null;

  return (
    <Link
      to={airline.canonical_path}
      data-testid={`top-airline-card-${airline.slug}`}
      className="group flex-shrink-0 w-[230px] sm:w-[245px] snap-start bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 overflow-hidden"
    >
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-center gap-3 mb-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden shrink-0"
            style={{ background: airline.brand_color || '#F1F5F9' }}
          >
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={`${airline.name} logo`}
                className="w-10 h-10 object-contain"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <Plane className="w-6 h-6 text-white/80" />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-[15px] font-bold text-[#1B375B] truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {airline.name}
            </p>
            <p className="text-xs text-gray-500 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {ISO2_FLAG(airline.country_iso2)} {airline.country || '—'}
            </p>
          </div>
        </div>

        <div className="flex items-center flex-wrap gap-1.5 mb-3 min-h-[24px]">
          {airline.alliance ? (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
              style={{ background: tone?.bg || '#1B375B', color: tone?.text || '#fff' }}
            >
              <Star className="w-2.5 h-2.5" /> {airline.alliance}
            </span>
          ) : airline.is_lowcost ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-orange-100 text-orange-700">
              Low-cost
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-[#E7EEEE] text-[#1B375B]">
              Full-service
            </span>
          )}
          {airline.iata && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider bg-gray-100 text-gray-700 font-mono">
              {airline.iata}
            </span>
          )}
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <span className="text-xs text-gray-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Explore
          </span>
          <ArrowRight className="w-4 h-4 text-[#659A9B] group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
};

const SkeletonCard = () => (
  <div className="flex-shrink-0 w-[230px] sm:w-[245px] snap-start bg-white rounded-2xl border border-gray-100 p-5 animate-pulse">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-12 h-12 rounded-xl bg-gray-200" />
      <div className="flex-1 space-y-2">
        <div className="h-3 bg-gray-200 rounded w-3/4" />
        <div className="h-2 bg-gray-100 rounded w-1/2" />
      </div>
    </div>
    <div className="h-5 bg-gray-100 rounded-full w-2/3 mb-3" />
    <div className="h-3 bg-gray-100 rounded w-full" />
  </div>
);

const TopAirlines = () => {
  const [airlines, setAirlines] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/public/homepage-strips`, {
          params: { airlines_limit: 6, destinations_limit: 1 },
          timeout: 6000,
        });
        if (cancelled) return;
        setAirlines(r.data?.top_airlines || []);
      } catch (e) {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (error) return null;  // graceful — don't render an empty/error strip
  const list = airlines || Array(6).fill(null);

  return (
    <section
      data-testid="top-airlines-section"
      className="bg-gradient-to-b from-white via-[#F7FAFA] to-white py-12 sm:py-14"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-5 sm:mb-6">
          <div>
            <p className="text-[11px] font-bold tracking-[0.18em] text-[#659A9B] uppercase mb-1.5">
              Fly with the best
            </p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1B375B]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Top Airlines
            </h2>
            <p className="text-sm text-gray-600 mt-1 max-w-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Trusted carriers serving Indian travelers.
            </p>
          </div>
          <Link
            to="/airlines"
            data-testid="top-airlines-view-all"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-[#1B375B] hover:text-[#659A9B] transition-colors whitespace-nowrap"
          >
            View all airlines <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div
          className="flex gap-3 sm:gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2 -mx-4 px-4 sm:mx-0 sm:px-0"
          style={{ scrollBehavior: 'smooth' }}
        >
          {list.map((a, i) =>
            a ? <AirlineCard key={a.slug} airline={a} /> : <SkeletonCard key={i} />
          )}
        </div>

        <Link
          to="/airlines"
          className="sm:hidden mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1B375B] hover:text-[#659A9B]"
        >
          View all airlines <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
};

export default TopAirlines;
